import * as EventEmitter from 'events';
import { v4 as uuidv4 } from 'uuid';
import * as signalR from '@microsoft/signalr';
import { Deferred } from 'ts-deferred';
import { parseEvent } from '@reachmedianetwork/eventing-abstractions';
import { serializeError } from 'serialize-error';
import { ReadableRpcReply } from './readableRpcReply';
import { encodeUserId, reachIamTokenRetrieval } from '@reachmedianetwork/iam-token';
import { Imprint } from './imprint';
function buildClientPropBag(defaultProps, ...additionalClientProps) {
    return {
        ...defaultProps,
        ...additionalClientProps.reduce((mergedProps, propBag) => {
            for (const [key, value] of Object.entries(propBag)) {
                if (key.toLowerCase() === 'clientversion') {
                    throw new Error("MercuryFxClientOptions additionalClientProps must not include a property named 'clientVersion'; this property is reserved for the MercuryFxClient");
                }
                mergedProps[key] = value;
            }
            return mergedProps;
        }, {}),
    };
}
function urlEncodePropBag(propBag) {
    return Object.entries(propBag)
        .map(([key, value]) => {
        return `props.${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
        .join('&');
}
/**
 * Token factory that uses the REACH IAM player password grant flow to acquire
 * a REACH IAM access token. This access token can be used to authenticate with
 * a Mercury Fx deployment.
 * @param options
 * @returns
 */
export const ReachIamTokenFactory = (factoryOptions) => async (options) => {
    var _a, _b, _c, _d;
    if (((_a = options.actor) === null || _a === void 0 ? void 0 : _a.type) !== 'player') {
        throw new Error(`Actor type '${String((_b = options.actor) === null || _b === void 0 ? void 0 : _b.type)}' is not supported.`);
    }
    const iam = reachIamTokenRetrieval()
        .setSecrets({
        clientId: factoryOptions.clientId,
        clientSecret: factoryOptions.clientSecret,
        sharedSecret: factoryOptions.sharedSecret,
    })
        .setPlayerId((_c = options.actor) === null || _c === void 0 ? void 0 : _c.name, (_d = options.actor) === null || _d === void 0 ? void 0 : _d.id);
    if (factoryOptions.linkedTokenEndpoint) {
        iam.setEndpoint(factoryOptions.linkedTokenEndpoint);
    }
    return iam.retrievePlayerToken();
};
/* eslint-enable @typescript-eslint/no-explicit-any */
export const MercuryFxEvents = [
    'open',
    'close',
    'error',
    'warn',
    'state changed',
    'debug',
    'subscribed',
    'push',
    'stream',
];
const AllowableStates = ['closed', 'open', 'connect', 'reconnect', 'fault'];
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class MercuryFxClient extends EventEmitter {
    constructor(options) {
        super();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this._rpcHandlers = new Map();
        this._currentState = 'closed';
        this._replyAwaiters = new Map();
        this._oneWayAwaiters = new Map();
        this._peerActorRpcAwaiters = new Map();
        /**
         * returns true if the client state is current one of `anyOf`.
         * @param anyOf
         * @returns
         */
        this._isInState = (anyOf) => {
            return anyOf.some((state) => state === this._currentState);
        };
        /**
         * sets the state of the client to `newState`. If the state is changed,
         * this method will emit a `state changed` event.
         * @param newState
         */
        this._setState = (newState) => {
            if (this._currentState !== newState) {
                const oldState = this._currentState;
                this._currentState = newState;
                this.emit('debug', this, `state changed from ${oldState} to ${newState}`);
                this.emit('state changed', this, newState, oldState);
            }
        };
        /**
         * asserts that the client state is one of `anyOf`. This method throws if
         * the state is not one of `anyOf`.
         * @param anyOf
         * @param message
         */
        this._requireState = (anyOf, message) => {
            if (!this._isInState(anyOf)) {
                throw new Error(message || `operation failed because client is not in state: ${anyOf.join(', ')}`);
            }
        };
        /**
         * Transitions the client state to a new state. Performs a state assertion
         * if requiredFromState is set.
         * @param newState
         * @param requiredFromState
         */
        this._transitionState = (newState, requiredFromState) => {
            if (requiredFromState) {
                this._requireState(requiredFromState, `cannot transition to state "${newState}" because client is not in state: ${requiredFromState.join(', ')}`);
            }
            this._setState(newState);
        };
        /**
         * Specifies the actor identity to use during negotiation.
         *
         * @param type player or user
         * @param name name of the actor
         * @param id instance id of the actor
         * @returns
         */
        this.withIdentity = (type, name, id) => {
            this._options.actor = {
                type,
                name,
                id,
                userId: encodeUserId({ type, name, id }),
            };
            return this;
        };
        /**
         * Registers an access token factory.
         * @param factory
         * @returns
         */
        this.withTokenFactory = (factory) => {
            this._options.tokenFactory = factory;
            return this;
        };
        /**
         * message handler for the `push` event. This method simply parses the
         * inbound message and emits the parsed data as an event.
         * @param event
         */
        this._onPush = (event) => {
            this.emit('push', parseEvent(event));
        };
        /**
         * handler for replies received from peer actors in response to a peer
         * actor RPC request originating from this client.
         * @param reply
         */
        // eslint-disable-next-line @typescript-eslint/require-await
        this._onReplyRpc = async (reply) => {
            this.emit('debug', this, `did receive RPC reply for ${reply.correlationId}`);
            const future = this._peerActorRpcAwaiters.get(reply.correlationId);
            if (future) {
                if (!reply.error) {
                    future.resolve(reply.reply);
                }
                else {
                    future.reject(reply.error);
                }
            }
            else {
                this.emit('warn', this, `no RPC awaiter for message ${reply.correlationId} ack; dropping on the floor`);
            }
        };
        /**
         * handles `callRpc` messages received from Mercury Fx. This signifies that
         * a peer actor has invoked an RPC defined by this client.
         * @param event
         */
        this._onCallRpc = async (event) => {
            this.emit('debug', this, `_onCallRpc invoked for ${event.name} from ${event.sender.userId}`);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const handler = this._rpcHandlers.get(event.name);
            if (!handler) {
                //sends response to caller with the error
                await this._sendRpcNack(event, new Error(`no handler defined for RPC "${event.name}"`));
            }
            else {
                try {
                    //call the RPC handler declared on this client
                    const args = event.args || [];
                    // eslint-disable-next-line prefer-spread, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
                    const handlerResult = await handler(...args);
                    await this._sendRpcReply(event, handlerResult);
                }
                catch (e) {
                    //send nack to caller indicating that the RPC implementation
                    //throw an error
                    if (e instanceof Error) {
                        await this._sendRpcNack(event, e);
                    }
                    else {
                        await this._sendRpcNack(event, new Error(`an unknown error occured: ${String(e)}`));
                    }
                }
            }
        };
        /**
         * sends an reply message back to the actor who called an RPC declared on
         * this client.
         * @param event
         * @param reply
         * @returns
         */
        this._sendRpcReply = async (event, reply) => {
            if (!event.replyReceipt) {
                this.emit('warn', this, `peer actor RPC ${event.name} (id=${event.id}) did not contain a reply receipt; dropping reply on the floor`);
                return;
            }
            const replyRpc = {
                recipient: event.sender,
                correlationId: event.id,
                replyReceipt: event.replyReceipt,
                reply,
            };
            await this.invokeOneWayRpc('replyPeerRpc', replyRpc);
        };
        /**
         * sends a nack message back to the actor who called an RPC declared on
         * this client.
         * @param event
         * @param err
         * @returns
         */
        this._sendRpcNack = async (event, err) => {
            if (!event.replyReceipt) {
                this.emit('warn', this, `peer actor RPC ${event.name} (id=${event.id}) did not contain a reply receipt; dropping reply on the floor`);
                return;
            }
            const serializedError = serializeError(err);
            const replyRpc = {
                recipient: event.sender,
                correlationId: event.id,
                replyReceipt: event.replyReceipt,
                error: {
                    ...serializedError,
                    message: serializedError.message || '',
                    name: serializedError.name || 'Error',
                },
            };
            await this.invokeOneWayRpc('replyPeerRpc', replyRpc);
        };
        /**
         * handles a reply message sent by Mercury Fx in response to a hub method
         * invocation.
         * @param type
         * @param args
         */
        this._onReply = (type, ...args) => {
            var _a, _b, _c, _d, _e, _f;
            const correlationId = args[0];
            this.emit('debug', this, 'reply received', { args });
            switch (type) {
                case 'ack':
                    this.emit('debug', this, `did receive ack for ${correlationId}`);
                    if (this._oneWayAwaiters.has(correlationId)) {
                        (_a = this._oneWayAwaiters.get(correlationId)) === null || _a === void 0 ? void 0 : _a.resolve();
                    }
                    else if (!this._replyAwaiters.has(correlationId)) {
                        this.emit('warn', this, `no awaiter for message ${correlationId} ack; dropping on the floor`);
                    }
                    break;
                case 'nack': {
                    this.emit('debug', this, `did receive nack for ${correlationId}`);
                    let didHandle = false;
                    if (this._oneWayAwaiters.has(correlationId)) {
                        didHandle = true;
                        (_b = this._oneWayAwaiters.get(correlationId)) === null || _b === void 0 ? void 0 : _b.reject(args[1]);
                    }
                    if (this._replyAwaiters.has(correlationId)) {
                        didHandle = true;
                        (_c = this._replyAwaiters.get(correlationId)) === null || _c === void 0 ? void 0 : _c.reject(args[1]);
                    }
                    if (this._peerActorRpcAwaiters.has(correlationId)) {
                        didHandle = true;
                        (_d = this._peerActorRpcAwaiters.get(correlationId)) === null || _d === void 0 ? void 0 : _d.reject(args[1]);
                    }
                    if (!didHandle) {
                        //it *could* be a stream reader... create a "fake" error
                        this.emit('stream', {
                            recipient: this._options.actor,
                            correlationId,
                            type: 'error',
                            error: args[1],
                        });
                        this.emit('warn', this, `no awaiter for message ${correlationId} nack; dropping on the floor`);
                    }
                    break;
                }
                case 'reply':
                    this.emit('debug', this, `did receive reply for ${correlationId}`);
                    if (this._oneWayAwaiters.has(correlationId)) {
                        (_e = this._oneWayAwaiters.get(correlationId)) === null || _e === void 0 ? void 0 : _e.resolve();
                        this._oneWayAwaiters.delete(correlationId);
                        this.emit('warn', this, `reply message received for one-message ${correlationId}; ack'ing instead`);
                    }
                    if (this._replyAwaiters.has(correlationId)) {
                        (_f = this._replyAwaiters.get(correlationId)) === null || _f === void 0 ? void 0 : _f.resolve(args[1]);
                    }
                    else {
                        this.emit('warn', this, `no awaiter for message ${correlationId} reply; dropping on the floor`);
                    }
                    break;
            }
        };
        /**
         * called when the signalr connection is closed.
         */
        this._onClose = ( /* err?: Error */) => {
            this._stopVingRoutine();
            this._transitionState('closed');
        };
        /**
         * called when the signalr connection is reconnected following a
         * disconnection.
         */
        this._onReconnected = ( /* connectionId?: string */) => {
            void this._startVingRoutine();
            this._transitionState('open');
        };
        /**
         * called when the connection to signalr is lost and the client is
         * attempting to reconnect.
         */
        this._onReconnecting = ( /* err?: Error */) => {
            this._stopVingRoutine();
            this._transitionState('reconnect');
        };
        /**
         * executes a remote procdure call on a peer actor.
         * @param invocation
         * @param timeout
         * @returns
         */
        this.callPeerActorRpc = async (invocation, timeout) => {
            var _a;
            this._requireState(['open']);
            if (!this._options.actor) {
                throw new Error('this client does not have a valid identity');
            }
            const correlationId = uuidv4();
            this.emit('debug', this, `invoking peer actor RPC "${invocation.name}" with correlationId ${correlationId}`);
            const startedAt = Date.now();
            const future = new Deferred();
            const timeoutHandle = setTimeout(() => {
                const elapsed = Date.now() - startedAt;
                future.reject(new Error(`operation "${invocation.name}" (correlationId=${correlationId}) timed out after ${elapsed} msec(s)`));
            }, timeout || 5000);
            this._peerActorRpcAwaiters.set(correlationId, future);
            const rpc = {
                id: correlationId,
                sender: this._options.actor,
                ...invocation,
            };
            void ((_a = this._connection) === null || _a === void 0 ? void 0 : _a.send('callPeerActorRpc', rpc));
            try {
                return await future.promise;
            }
            finally {
                clearTimeout(timeoutHandle);
                this._peerActorRpcAwaiters.delete(correlationId);
            }
        };
        /**
         * Initiates a connection to the mercuryfx signalr hub.
         */
        this.connect = (options) => {
            if (!this._initialConnectDfd) {
                this._initialConnectDfd = new Deferred();
                this._transitionState('connect', ['closed']);
                this._connect({
                    attempt: 1,
                    ...(options || {
                        enableReconnect: true,
                        backoffFactor: 1.5,
                        maxRetryDelay: 60000,
                    }),
                }).catch(() => {
                    //nobody is listening...
                });
            }
            return this._initialConnectDfd.promise;
        };
        this._connect = async (state) => {
            var _a, _b;
            if (!this._options.actor) {
                throw new Error('no actor was specified');
            }
            const clientPropBag = buildClientPropBag({
                clientVersion: Imprint.version,
            }, this._options.additionalClientProps || {});
            const tokenFactory = this._options.tokenFactory
                ? {
                    accessTokenFactory: () => {
                        return this._options.tokenFactory
                            ? this._options.tokenFactory(this._options, clientPropBag)
                            : '';
                    },
                }
                : {};
            //future code changes could result in empty prop bag, so we'll do the
            //right thing and ensure that there is a query string before slapping
            //an ampersand on the end
            const clientPropsQueryStringSlug = urlEncodePropBag(clientPropBag);
            const urlQueryStringSlug = `?userId=${this._options.actor.userId}` +
                (clientPropsQueryStringSlug.length > 0 ? `&${clientPropsQueryStringSlug}` : '');
            const connection = new signalR.HubConnectionBuilder()
                .withUrl(`${String(this._options.baseUrl)}/api${urlQueryStringSlug}`, {
                transport: signalR.HttpTransportType.WebSockets,
                ...tokenFactory,
            })
                .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    const jitterProportion = 0.9 + Math.random() * 0.2;
                    const nextDelayBase = Math.pow(1.5, retryContext.previousRetryCount) * 1000;
                    const nextDelay = Math.round(nextDelayBase * jitterProportion);
                    return Math.min(120000, nextDelay);
                },
            })
                .build();
            connection.on('push', this._onPush);
            connection.on('reply', this._onReply);
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            connection.on('callRpc', this._onCallRpc);
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            connection.on('replyRpc', this._onReplyRpc);
            connection.on('stream', (event) => this.emit('stream', event));
            connection.onclose(this._onClose);
            connection.onreconnected(this._onReconnected);
            connection.onreconnecting(this._onReconnecting);
            try {
                await connection.start();
                this._setState('open');
                void this._startVingRoutine();
                this._connection = connection;
                (_a = this._initialConnectDfd) === null || _a === void 0 ? void 0 : _a.resolve();
            }
            catch (e) {
                if (state.enableReconnect) {
                    const delayForAttempt = Math.round(Math.pow(state.backoffFactor, state.attempt++) * 1000);
                    const nextDelay = Math.min(delayForAttempt, state.maxRetryDelay);
                    this._reconnectTimeout = setTimeout(() => {
                        this._connect(state).catch(() => {
                            //no body is listening...
                        });
                    }, nextDelay);
                }
                else {
                    (_b = this._initialConnectDfd) === null || _b === void 0 ? void 0 : _b.reject(e);
                    this._initialConnectDfd = undefined;
                }
                this.emit('error', this, this._currentState, e);
                throw e;
            }
        };
        /**
         * terminates the ving routine.
         */
        this._stopVingRoutine = () => {
            if (this._vingHandle) {
                clearInterval(this._vingHandle);
                this._vingHandle = undefined;
            }
        };
        this._executeSingleVing = async () => {
            if (this._currentState === 'open') {
                try {
                    await this.fireAndForgetRpc('ving');
                }
                catch (e) {
                    // eslint-disable-line @typescript-eslint/no-explicit-any
                    if (e instanceof Error) {
                        this.emit('debug', this, `ving RPC failed: ${e.message}`);
                    }
                    else {
                        this.emit('debug', this, `ving RPC failed: ${String(e)}`);
                    }
                }
            }
        };
        /**
         * starts the ving routine.
         */
        // eslint-disable-next-line @typescript-eslint/require-await
        this._startVingRoutine = async () => {
            this._stopVingRoutine();
            const effectiveVingInterval = this._options.vingInterval || 15000;
            const [delayMin, delayMax] = this._options.vingRandomizationRange || [
                0,
                this._options.vingInterval || effectiveVingInterval,
            ];
            const initialDelay = Math.floor(Math.random() * (delayMax - delayMin)) + delayMin;
            //wait a random amount of time between delayMin and delayMax before
            //executing the first "ving"
            this._vingHandle = setTimeout(() => {
                void (async () => {
                    await this._executeSingleVing();
                    //execute 1 ving every effectiveVingInterval ms thereafter
                    this._vingHandle = setInterval(() => {
                        void this._executeSingleVing();
                    }, effectiveVingInterval);
                })();
            }, initialDelay);
        };
        /**
         * disconnects from the mercuryfx signalr hub.
         */
        this.disconnect = async () => {
            var _a;
            if (this._reconnectTimeout) {
                clearTimeout(this._reconnectTimeout);
                this._reconnectTimeout = undefined;
            }
            if (this._initialConnectDfd) {
                this._initialConnectDfd.reject(new Error('connect operation aborted'));
                this._initialConnectDfd = undefined;
            }
            await ((_a = this._connection) === null || _a === void 0 ? void 0 : _a.stop());
        };
        this._options = {
            baseUrl: 'https://mercuryfx.reachcm.com',
            ...(options || {}),
        };
        this.emit('debug', this, 'MercuryFxClient created', { options });
        this.on('state changed', (sender, newState) => {
            switch (newState) {
                case 'open':
                    this.emit('open', this);
                    break;
                case 'closed':
                    this.emit('close', this);
                    break;
            }
        });
    }
    /**
     * returns the current state of the client.
     */
    get state() {
        return this._currentState;
    }
    /**
     * Returns the underlying SignalR connection id.
     */
    get connectionId() {
        if (!this._connection) {
            throw new Error('connection has not been started');
        }
        if (this._connection.state !== signalR.HubConnectionState.Connected) {
            throw new Error(`signalr connection is in incorrect state "${this._connection.state}"; it must be in state "${signalR.HubConnectionState.Connected}"`);
        }
        if (!this._connection.connectionId) {
            throw new Error('signalr connectionId is not available');
        }
        return this._connection.connectionId;
    }
    getMyActors() {
        if (!this._options.actor) {
            throw new Error("cannot getMyActors; I don't have an identity yet");
        }
        return this.invokeStreamRpc('queryActors', this._options.actor);
    }
    /**
     * declares a handler function to be called when a peer actor calls an RPC
     * on this client.
     * @param name
     * @param handler
     */
    declareRpc(name, handler) {
        if (this._rpcHandlers.has(name)) {
            throw new Error(`RPC handler "${name}" is already declared`);
        }
        this._rpcHandlers.set(name, handler);
    }
    async fireAndForgetRpc(method, payload) {
        var _a;
        this._requireState(['open']);
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const argsCorrelationId = typeof (payload === null || payload === void 0 ? void 0 : payload.correlationId) === 'string'
            ? payload === null || payload === void 0 ? void 0 : payload.correlationId
            : undefined;
        const correlationId = argsCorrelationId || uuidv4();
        /* eslint-enable @typescript-eslint/no-explicit-any */
        this.emit('debug', this, `invoking fire-and-forget RPC ${method} with correlationId ${correlationId}`);
        let timeout = undefined;
        const future = new Deferred();
        const cleanupAwaiter = () => {
            this._oneWayAwaiters.delete(correlationId);
            if (null != timeout) {
                clearTimeout(timeout);
                timeout = undefined;
            }
        };
        this._oneWayAwaiters.set(correlationId, future);
        void future.promise.then(cleanupAwaiter).catch(cleanupAwaiter);
        timeout = setTimeout(cleanupAwaiter, 30000);
        await ((_a = this._connection) === null || _a === void 0 ? void 0 : _a.send(method, {
            ...payload,
            correlationId,
        }));
    }
    async invokeOneWayRpc(method, payloadOrTimeout, timeout) {
        var _a;
        this._requireState(['open']);
        let payload = undefined;
        switch (typeof payloadOrTimeout) {
            case 'object':
                payload = payloadOrTimeout;
                break;
            case 'number':
                timeout = payloadOrTimeout;
                break;
        }
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const argsCorrelationId = typeof (payload === null || payload === void 0 ? void 0 : payload.correlationId) === 'string'
            ? payload === null || payload === void 0 ? void 0 : payload.correlationId
            : undefined;
        const correlationId = argsCorrelationId || uuidv4();
        /* eslint-enable @typescript-eslint/no-explicit-any */
        this.emit('debug', this, `invoking one-way RPC ${method} with correlationId ${correlationId}`);
        const startedAt = Date.now();
        const future = new Deferred();
        const timeoutHandle = setTimeout(() => {
            const elapsed = Date.now() - startedAt;
            future.reject(new Error(`operation "${method}" (correlationId=${correlationId}) timed out after ${elapsed} msec(s)`));
        }, timeout || 5000);
        this._oneWayAwaiters.set(correlationId, future);
        void ((_a = this._connection) === null || _a === void 0 ? void 0 : _a.send(method, {
            ...payload,
            correlationId,
        }));
        try {
            await future.promise;
        }
        finally {
            clearTimeout(timeoutHandle);
            this._oneWayAwaiters.delete(correlationId);
        }
    }
    invokeStreamRpc(method, payload) {
        var _a;
        this._requireState(['open']);
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const argsCorrelationId = typeof (payload === null || payload === void 0 ? void 0 : payload.correlationId) === 'string'
            ? payload === null || payload === void 0 ? void 0 : payload.correlationId
            : undefined;
        const correlationId = argsCorrelationId || uuidv4();
        /* eslint-enable @typescript-eslint/no-explicit-any */
        this.emit('debug', this, `invoking stream RPC ${method} with correlationId ${correlationId}`);
        const stream = new ReadableRpcReply(this, correlationId);
        void ((_a = this._connection) === null || _a === void 0 ? void 0 : _a.send(method, {
            ...payload,
            correlationId,
        }));
        return stream;
    }
    async invokeRpc(method, payloadOrTimeout, timeout) {
        var _a;
        this._requireState(['open']);
        let payload = undefined;
        switch (typeof payloadOrTimeout) {
            case 'object':
                payload = payloadOrTimeout;
                break;
            case 'number':
                timeout = payloadOrTimeout;
                break;
        }
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const argsCorrelationId = typeof (payload === null || payload === void 0 ? void 0 : payload.correlationId) === 'string'
            ? payload === null || payload === void 0 ? void 0 : payload.correlationId
            : undefined;
        const correlationId = argsCorrelationId || uuidv4();
        /* eslint-enable @typescript-eslint/no-explicit-any */
        this.emit('debug', this, `invoking two-way RPC ${method} with correlationId ${correlationId}`);
        const startedAt = Date.now();
        const future = new Deferred();
        const timeoutHandle = setTimeout(() => {
            const elapsed = Date.now() - startedAt;
            future.reject(new Error(`operation "${method}" (correlationId=${correlationId}) timed out after ${elapsed} msec(s)`));
        }, timeout || 5000);
        this._replyAwaiters.set(correlationId, future);
        void ((_a = this._connection) === null || _a === void 0 ? void 0 : _a.send(method, {
            ...payload,
            correlationId,
        }));
        try {
            return await future.promise;
        }
        finally {
            clearTimeout(timeoutHandle);
            this._replyAwaiters.delete(correlationId);
        }
    }
    /**
     * Enables auto-subscribe behavior on the client. When auto-subscribe is
     * active, any time the client's state transitions to "open", the client
     * will automatically execute the createSubscriptions RPC on the next JS
     * tick.
     * @param timeout (optional) msec time for each createSubscription call
     * @returns
     */
    useAutoSubscribe(timeout) {
        let subscribeTimer = undefined;
        let subscribeBackoff = 1000;
        const createSubscriptionsRoutine = async () => {
            if (!this._isInState(['open'])) {
                return;
            }
            this.emit('debug', this, 'creating subscriptions');
            try {
                const start = Date.now();
                const response = await this.invokeRpc('createSubscriptions', timeout || 5000);
                const rtt = Date.now() - start;
                subscribeBackoff = 1000;
                if (!this._isInState(['open'])) {
                    return;
                }
                this.emit('debug', this, 'createSubscriptions call completed', { rtt, response });
                this.emit('subscribed', this, response.subscriptions.map((subscription) => ({
                    ...subscription,
                    expires_at: new Date(subscription.expires_at),
                })));
            }
            catch (e) {
                if (!this._isInState(['open'])) {
                    return;
                }
                subscribeBackoff = Math.min(subscribeBackoff * 1.5, 60000);
                this.emit('debug', this, `failed to create subscriptions, will retry in ${subscribeBackoff}`);
                subscribeTimer = setTimeout(() => void createSubscriptionsRoutine(), subscribeBackoff);
            }
        };
        // eslint-disable-next-line @typescript-eslint/require-await
        const stateChangeHandler = async (sender, newState) => {
            if (subscribeTimer) {
                clearInterval(subscribeTimer);
                subscribeTimer = undefined;
            }
            switch (newState) {
                case 'open':
                    this.emit('debug', this, 'channel is open, will fire subscription creation routine');
                    subscribeTimer = setTimeout(() => void createSubscriptionsRoutine(), 0);
                    break;
            }
        };
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.on('state changed', stateChangeHandler);
        return this;
    }
}
export default MercuryFxClient;
//# sourceMappingURL=mercuryFxClient.js.map